import { useEffect, useState } from "react";
import { Plug, CheckCircle2, XCircle, Loader2, Trash2, CalendarDays, Copy, Plus, ExternalLink, Download, Link2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "../../lib/api";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [smoobuKey, setSmoobuKey] = useState("");
  const [pp, setPp] = useState({ paypal_client_id: "", paypal_secret: "", paypal_mode: "sandbox" });
  const [testing, setTesting] = useState(false);
  const [editingSmoobu, setEditingSmoobu] = useState(false);
  const [props, setProps] = useState([]);

  const load = () => api.get("/admin/settings").then((r) => {
    setS(r.data);
    setPp({ paypal_client_id: r.data.paypal_client_id || "", paypal_secret: "", paypal_mode: r.data.paypal_mode || "sandbox" });
    setEditingSmoobu(!r.data.smoobu_connected);
  });
  const loadProps = () => api.get("/admin/apartments").then((r) => setProps(r.data || []));
  useEffect(() => { load(); loadProps(); }, []);

  const saveSmoobu = async () => {
    if (!smoobuKey.trim()) return toast.error("Inserisci una API key");
    try { await api.put("/admin/settings/smoobu", { smoobu_api_key: smoobuKey }); toast.success("Chiave Smoobu salvata"); setSmoobuKey(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteSmoobu = async () => {
    if (!window.confirm("Eliminare la chiave Smoobu? La sincronizzazione verrà disattivata.")) return;
    try { await api.delete("/admin/settings/smoobu"); toast.success("Chiave Smoobu eliminata"); setSmoobuKey(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const testSmoobu = async () => {
    setTesting(true);
    try { await api.post("/admin/settings/smoobu/test"); toast.success("Connessione Smoobu riuscita!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };
  const savePaypal = async () => {
    try { await api.put("/admin/settings/paypal", pp); toast.success("Credenziali PayPal salvate"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePaypalEnabled = async (val) => {
    setS((prev) => ({ ...prev, paypal_enabled: val }));
    try { await api.put("/admin/settings/paypal-enabled", { active: val }); toast.success(val ? "PayPal mostrato al checkout" : "PayPal nascosto al checkout"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };
  const [testingPp, setTestingPp] = useState(false);
  const testPaypal = async () => {
    setTestingPp(true);
    try { const { data } = await api.post("/admin/settings/paypal/test"); toast.success(`Connessione PayPal riuscita (ambiente: ${data.mode})`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail), { duration: 9000 }); }
    finally { setTestingPp(false); }
  };

  if (!s) return <Loader2 className="animate-spin text-fir" />;

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-4xl text-fir mb-2">Integrazioni</h1>
      <p className="text-fir-deep/60 mb-8">Collega Smoobu, i calendari iCal (Booking/Holidu) e PayPal.</p>

      {/* SMOOBU */}
      <section className="bg-white rounded-2xl p-7 border border-stone mb-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl text-fir flex items-center gap-2"><Plug size={22} className="text-wood" /> Smoobu Channel Manager</h2>
          <Status ok={s.smoobu_connected} okText="Connesso" koText="Non connesso" />
        </div>

        {s.smoobu_connected && !editingSmoobu ? (
          <div className="flex items-center justify-between gap-3 bg-ivory rounded-xl p-4 border border-stone">
            <div>
              <p className="text-sm text-fir-deep/60">Chiave attiva</p>
              <p className="font-mono text-fir">{s.smoobu_api_key_masked}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid="smoobu-test-btn" onClick={testSmoobu} disabled={testing}
                className="px-4 py-2 rounded-xl border border-fir text-fir hover:bg-fir hover:text-ivory transition-colors flex items-center gap-2 disabled:opacity-40">
                {testing ? <Loader2 className="animate-spin" size={16} /> : "Testa"}
              </button>
              <button data-testid="smoobu-edit-btn" onClick={() => setEditingSmoobu(true)}
                className="px-4 py-2 rounded-xl border border-fir text-fir hover:bg-fir hover:text-ivory transition-colors">Modifica</button>
              <button data-testid="smoobu-delete-btn" onClick={deleteSmoobu}
                className="px-4 py-2 rounded-xl border border-red-300 text-red-600 hover:bg-red-600 hover:text-white transition-colors flex items-center gap-2">
                <Trash2 size={16} /> Elimina
              </button>
            </div>
          </div>
        ) : (
          <>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Smoobu API Key (v2)</label>
            <input data-testid="smoobu-key-input" value={smoobuKey} onChange={(e) => setSmoobuKey(e.target.value)}
              placeholder="Incolla la tua API key Smoobu" className="input-alpine w-full mt-1.5 mb-4" />
            <div className="flex gap-3">
              <button data-testid="smoobu-save-btn" onClick={saveSmoobu} className="gold-btn px-6 py-2.5 rounded-xl">Salva</button>
              {s.smoobu_connected && (
                <button data-testid="smoobu-cancel-btn" onClick={() => { setEditingSmoobu(false); setSmoobuKey(""); }}
                  className="px-6 py-2.5 rounded-xl border border-stone text-fir-deep/70 hover:bg-ivory transition-colors">Annulla</button>
              )}
            </div>
          </>
        )}

        <div className="mt-4 text-xs text-fir-deep/60 bg-ivory rounded-xl p-4 border border-stone leading-relaxed">
          <p className="font-medium text-fir mb-1">Come ottenere la chiave:</p>
          <p>Accedi a Smoobu → <b>Impostazioni</b> → <b>Per Sviluppatori / API</b> → genera/copia la <b>API key</b> e incollala qui. Il pricing IA invierà automaticamente le tariffe a Smoobu.</p>
        </div>

        {s.smoobu_connected && <SmoobuSync props={props} onMapped={loadProps} />}
      </section>

      {/* iCAL */}
      <section className="bg-white rounded-2xl p-7 border border-stone mb-6">
        <h2 className="font-display text-2xl text-fir flex items-center gap-2 mb-1"><CalendarDays size={22} className="text-wood" /> Calendari iCal (Booking.com / Holidu / Airbnb)</h2>
        <p className="text-sm text-fir-deep/60 mb-5">Booking e Holidu non offrono API per singolo host: usa lo scambio di calendari <b>.ics</b>. <b>Importa</b> i loro URL per bloccare le date già prenotate; <b>esporta</b> il tuo link per sincronizzare le nostre prenotazioni sulle loro piattaforme.</p>
        {props.length === 0 && <p className="text-sm text-fir-deep/50">Nessuna struttura trovata.</p>}
        <div className="space-y-5">
          {props.map((p) => <PropertyIcal key={p.id} prop={p} onSaved={loadProps} />)}
        </div>
      </section>

      {/* PAYPAL */}
      <section className="bg-white rounded-2xl p-7 border border-stone">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl text-fir">PayPal Checkout</h2>
          <Status ok={s.paypal_configured} okText="Configurato" koText="Non configurato" />
        </div>
        <label className="flex items-center justify-between gap-3 mb-5 rounded-xl border border-stone bg-stone/30 px-4 py-3 cursor-pointer" data-testid="paypal-enabled-toggle">
          <span className="text-sm text-fir">
            Mostra PayPal al checkout
            <span className="block text-xs text-fir-deep/55 font-normal">Disattivalo per nascondere il pulsante PayPal ai clienti (resta attivo il pagamento con carta Stripe).</span>
          </span>
          <input type="checkbox" checked={s.paypal_enabled !== false} onChange={(e) => togglePaypalEnabled(e.target.checked)} className="accent-gold w-5 h-5" />
        </label>
        <div className="grid gap-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Client ID</label>
            <input data-testid="paypal-clientid-input" value={pp.paypal_client_id}
              onChange={(e) => setPp({ ...pp, paypal_client_id: e.target.value })} className="input-alpine w-full mt-1.5" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Secret</label>
            <input data-testid="paypal-secret-input" type="password" value={pp.paypal_secret}
              onChange={(e) => setPp({ ...pp, paypal_secret: e.target.value })} placeholder="••••••••" className="input-alpine w-full mt-1.5" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-fir-deep/60">Ambiente</label>
            <select data-testid="paypal-mode-select" value={pp.paypal_mode}
              onChange={(e) => setPp({ ...pp, paypal_mode: e.target.value })} className="input-alpine w-full mt-1.5">
              <option value="sandbox">Sandbox (test)</option>
              <option value="live">Live (produzione)</option>
            </select>
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button data-testid="paypal-save-btn" onClick={savePaypal} className="gold-btn px-6 py-2.5 rounded-xl">Salva credenziali PayPal</button>
          <button data-testid="paypal-test-btn" onClick={testPaypal} disabled={testingPp || !s.paypal_configured}
            className="px-6 py-2.5 rounded-xl border border-fir text-fir hover:bg-fir hover:text-ivory transition-colors flex items-center gap-2 disabled:opacity-40">
            {testingPp ? <Loader2 className="animate-spin" size={16} /> : "Testa connessione"}
          </button>
        </div>
        <p className="text-xs text-fir-deep/50 mt-3">Le credenziali <b>Sandbox</b> (test) e <b>Live</b> (produzione) sono diverse: assicurati che l'ambiente selezionato corrisponda alle chiavi inserite. Trovi Client ID e Secret in developer.paypal.com → My Apps &amp; Credentials.</p>
      </section>

      <ChangePassword />
    </div>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (nw.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    if (nw !== nw2) return toast.error("Le due nuove password non coincidono");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password aggiornata con successo");
      setCur(""); setNw(""); setNw2("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <section className="bg-white rounded-2xl p-6 border border-stone mt-8" data-testid="change-password-card">
      <h2 className="font-display text-2xl text-fir mb-1 flex items-center gap-2"><KeyRound size={20} className="text-gold" /> Cambia password admin</h2>
      <p className="text-xs text-fir-deep/60 mb-4">Aggiorna la password di accesso al pannello (minimo 8 caratteri). Resta valida anche dopo gli aggiornamenti del sito.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <input type="password" data-testid="cp-current" value={cur} onChange={(e) => setCur(e.target.value)} placeholder="Password attuale" className="input-alpine" />
        <input type="password" data-testid="cp-new" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="Nuova password" className="input-alpine" />
        <input type="password" data-testid="cp-new2" value={nw2} onChange={(e) => setNw2(e.target.value)} placeholder="Ripeti nuova password" className="input-alpine" />
      </div>
      <button data-testid="cp-submit" onClick={submit} disabled={saving || !cur || !nw} className="gold-btn px-5 py-2.5 rounded-xl mt-4 disabled:opacity-40">
        {saving ? "Salvataggio…" : "Aggiorna password"}
      </button>
    </section>
  );
}

function SmoobuSync({ props, onMapped }) {
  const [smoobuApts, setSmoobuApts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadSmoobuApts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/smoobu/apartments");
      const raw = Array.isArray(data) ? data : (data.apartments || []);
      setSmoobuApts(raw.map((a) => (typeof a === "object" ? { id: String(a.id), name: a.name || `Appartamento ${a.id}` } : { id: String(a), name: `Appartamento ${a}` })));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSmoobuApts([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadSmoobuApts(); }, []);

  // build our-entity options
  const options = [];
  (props || []).forEach((p) => {
    if (p.type === "hotel" && p.room_types?.length) {
      p.room_types.forEach((rt) => options.push({ value: `${p.id}|${rt.id}`, label: `${p.title} · ${rt.name}` }));
    } else {
      options.push({ value: p.id, label: p.title });
    }
  });

  const currentValueFor = (sid) => {
    for (const p of props || []) {
      if (String(p.smoobu_id || "") === sid) return p.id;
      for (const rt of p.room_types || []) if (String(rt.smoobu_id || "") === sid) return `${p.id}|${rt.id}`;
    }
    return "";
  };

  const setMap = async (sid, value, prevValue) => {
    try {
      // clear previous mapping if changed
      if (prevValue && prevValue !== value) {
        const [pp, rr] = prevValue.split("|");
        await api.put("/admin/smoobu/map", { smoobu_id: "", property_id: pp, room_type_id: rr || null });
      }
      if (value) {
        const [pp, rr] = value.split("|");
        await api.put("/admin/smoobu/map", { smoobu_id: sid, property_id: pp, room_type_id: rr || null });
      }
      toast.success("Mappatura aggiornata");
      onMapped && onMapped();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/smoobu/import");
      let msg = `${data.imported} importate, ${data.updated} aggiornate`;
      if (data.removed) msg += `, ${data.removed} rimosse (cancellate su Smoobu)`;
      if (data.skipped_unmapped) msg += ` — ${data.skipped_unmapped} saltate (appartamenti Smoobu non mappati: ${data.unmapped_smoobu_ids.join(", ")})`;
      toast.success(msg, { duration: 8000 });
      onMapped && onMapped();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const webhookUrl = `${API}/webhooks/smoobu`;
  const copyWebhook = () => { navigator.clipboard.writeText(webhookUrl); toast.success("URL webhook copiato"); };

  return (
    <div className="mt-5 pt-5 border-t border-stone">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display text-lg text-fir flex items-center gap-2"><Link2 size={18} className="text-wood" /> Sincronizzazione prenotazioni</h3>
        <button data-testid="smoobu-import-btn" onClick={runImport} disabled={importing}
          className="gold-btn px-5 py-2 rounded-xl flex items-center gap-2 disabled:opacity-50">
          {importing ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />} Importa prenotazioni
        </button>
      </div>
      <p className="text-xs text-fir-deep/60 mb-4">Importa le prenotazioni già presenti su Smoobu. Le cancellazioni fatte su Smoobu vengono rimosse automaticamente. Sync di sicurezza ogni 5 min; per l'aggiornamento <b>in tempo reale</b> configura il webhook qui sotto.</p>

      <div className="mb-4 bg-ivory rounded-xl p-4 border border-stone">
        <p className="text-xs uppercase tracking-wide text-fir-deep/60 mb-1.5">Webhook tempo reale — incolla su Smoobu</p>
        <div className="flex gap-2">
          <input readOnly value={webhookUrl} data-testid="smoobu-webhook-url" className="input-alpine flex-1 font-mono text-xs" />
          <button onClick={copyWebhook} className="px-3 py-2 rounded-lg border border-fir text-fir hover:bg-fir hover:text-ivory transition-colors flex items-center gap-1.5"><Copy size={15} /> Copia</button>
        </div>
        <p className="text-[11px] text-fir-deep/50 mt-2">Su Smoobu → <b>Impostazioni</b> → <b>Per Sviluppatori / API</b> → <b>Webhooks</b>: incolla questo URL. Ogni nuova prenotazione, modifica o cancellazione aggiornerà il sito istantaneamente.</p>
      </div>

      <p className="text-xs uppercase tracking-wide text-fir-deep/60 mb-2">Mappatura appartamenti Smoobu → tue strutture</p>
      {loading && <Loader2 className="animate-spin text-fir" size={18} />}
      {smoobuApts && smoobuApts.length === 0 && !loading && <p className="text-sm text-fir-deep/50">Nessun appartamento trovato su Smoobu (o chiave non valida).</p>}
      <div className="space-y-2">
        {(smoobuApts || []).map((a) => {
          const cur = currentValueFor(a.id);
          return (
            <div key={a.id} data-testid={`smoobu-map-row-${a.id}`} className="flex items-center gap-3 bg-ivory rounded-xl p-3 border border-stone">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-fir truncate">{a.name}</p>
                <p className="text-[11px] text-fir-deep/50 font-mono">ID Smoobu: {a.id}</p>
              </div>
              <select data-testid={`smoobu-map-select-${a.id}`} value={cur}
                onChange={(e) => setMap(a.id, e.target.value, cur)} className="input-alpine w-64">
                <option value="">— Non mappato —</option>
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PropertyIcal({ prop, onSaved }) {
  const [feeds, setFeeds] = useState(prop.ical_feeds?.length ? prop.ical_feeds : [{ name: "", url: "" }]);
  const [savedFeeds, setSavedFeeds] = useState(prop.ical_feeds || []);
  const [saving, setSaving] = useState(false);
  const [testingIdx, setTestingIdx] = useState(null);
  const exportUrl = `${API}/ical/${prop.id}.ics`;

  const norm = (arr) => JSON.stringify((arr || []).filter((f) => (f.url || "").trim()).map((f) => ({ name: (f.name || "").trim(), url: f.url.trim() })));
  const dirty = norm(feeds) !== norm(savedFeeds);

  const setFeed = (i, field, val) => setFeeds(feeds.map((f, idx) => (idx === i ? { ...f, [field]: val } : f)));
  const addFeed = () => setFeeds([...feeds, { name: "", url: "" }]);
  const removeFeed = (i) => setFeeds(feeds.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const clean = feeds.filter((f) => f.url.trim());
      const { data } = await api.put(`/admin/apartments/${prop.id}/ical-feeds`, { feeds: clean });
      setSavedFeeds(data.ical_feeds || clean);
      const imp = data.import || {};
      const n = (imp.imported || 0) + (imp.updated || 0);
      toast.success(n > 0
        ? `Calendari salvati · ${imp.imported || 0} prenotazioni importate, ${imp.updated || 0} aggiornate (visibili nel Calendario)`
        : `Calendari salvati per ${prop.title}`);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const autoSave = () => { if (dirty && !saving) save(); };
  const testFeed = async (i) => {
    const url = (feeds[i]?.url || "").trim();
    if (!url) return toast.error("Inserisci prima l'URL del calendario");
    setTestingIdx(i);
    try {
      const { data } = await api.post("/admin/ical/test", { url });
      toast.success(`Calendario valido ✓ ${data.events} prenotazioni/blocchi trovati`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { duration: 11000 }); }
    finally { setTestingIdx(null); }
  };
  const copyExport = () => { navigator.clipboard.writeText(exportUrl); toast.success("Link di export copiato"); };

  return (
    <div data-testid={`ical-property-${prop.id}`} className="border border-stone rounded-xl p-5 bg-ivory/40">
      <div className="flex items-center justify-between mb-3">
        <p className="font-display text-lg text-fir">{prop.title}
          <span className="ml-2 text-[11px] uppercase tracking-wide text-fir-deep/50">{prop.type === "hotel" ? "Hotel" : "Appartamento"}</span>
        </p>
        {!prop.active && <span className="text-xs text-amber-600">In pausa</span>}
      </div>

      <label className="text-xs uppercase tracking-wide text-fir-deep/60">Import — URL iCal esterni</label>
      <div className="space-y-2 mt-1.5 mb-3">
        {feeds.map((f, i) => (
          <div key={i} className="border border-stone/60 rounded-lg p-2.5 bg-white/60 space-y-2">
            <div className="flex gap-2 items-center">
              <input data-testid={`ical-name-${prop.id}-${i}`} value={f.name} onChange={(e) => setFeed(i, "name", e.target.value)} onBlur={autoSave}
                placeholder="Nome (es. Booking, Holidu)" className="input-alpine flex-1" style={{ minWidth: 0 }} />
              <button data-testid={`ical-test-${prop.id}-${i}`} onClick={() => testFeed(i)} disabled={testingIdx === i}
                className="px-3 py-2 rounded-lg border border-fir text-fir text-sm hover:bg-fir hover:text-ivory transition-colors whitespace-nowrap disabled:opacity-40">
                {testingIdx === i ? "…" : "Testa"}
              </button>
              <button onClick={() => removeFeed(i)} className="px-2 text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
            </div>
            <input data-testid={`ical-url-${prop.id}-${i}`} value={f.url} onChange={(e) => setFeed(i, "url", e.target.value)} onBlur={autoSave}
              placeholder="https://...ical/export.ics — incolla qui il link del calendario" className="input-alpine w-full font-mono text-xs" />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid={`ical-add-${prop.id}`} onClick={addFeed} className="px-3 py-1.5 rounded-lg border border-stone text-fir text-sm flex items-center gap-1.5 hover:bg-white">
          <Plus size={14} /> Aggiungi URL
        </button>
        <button data-testid={`ical-save-${prop.id}`} onClick={save} disabled={saving || !dirty}
          className={`px-4 py-1.5 rounded-lg text-sm transition-all ${dirty ? "gold-btn" : "border border-stone text-fir-deep/50"}`}>
          {saving ? "Salvataggio…" : dirty ? "Salva calendari" : "Salvato ✓"}
        </button>
        {dirty && !saving && (
          <span data-testid={`ical-unsaved-${prop.id}`} className="text-xs text-amber-600 flex items-center gap-1 self-center">
            ● Modifiche non salvate (si salva anche uscendo dal campo)
          </span>
        )}
      </div>

      <div className="mt-4 pt-3 border-t border-stone">
        <label className="text-xs uppercase tracking-wide text-fir-deep/60">Export — incolla questo link su Booking/Holidu</label>
        <div className="flex gap-2 mt-1.5">
          <input readOnly value={exportUrl} className="input-alpine flex-1 font-mono text-xs" data-testid={`ical-export-${prop.id}`} />
          <button onClick={copyExport} className="px-3 py-2 rounded-lg border border-fir text-fir hover:bg-fir hover:text-ivory transition-colors flex items-center gap-1.5">
            <Copy size={15} /> Copia
          </button>
          <a href={exportUrl} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border border-stone text-fir-deep/70 hover:bg-white flex items-center"><ExternalLink size={15} /></a>
        </div>
      </div>
    </div>
  );
}

function Status({ ok, okText, koText }) {
  return ok
    ? <span className="flex items-center gap-1.5 text-green-600 text-sm font-medium"><CheckCircle2 size={16} /> {okText}</span>
    : <span className="flex items-center gap-1.5 text-fir-deep/40 text-sm"><XCircle size={16} /> {koText}</span>;
}
